const STORAGE_VERSION = 'v1';
const STORAGE_PREFIX = `climorax:caderno-notificacoes:${STORAGE_VERSION}`;
const CHANGE_EVENT = 'climorax:caderno-notificacoes-alteradas';

function normalizeId(value) {
  return value == null ? '' : String(value);
}

function normalizedNotificationText(value) {
  return String(value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('pt-BR');
}

export function deduplicateOperationalNotifications(notifications = []) {
  const seen = new Set();
  return (notifications || []).filter(notification => {
    const timestamp = notification.createdAt || notification.criada_em || notification.criado_em;
    const date = new Date(timestamp);
    const timeKey = Number.isNaN(date.getTime()) ? String(timestamp || '') : date.toISOString().slice(0, 16);
    const property = notification.propertyName || notification.dados?.propriedade_nome || notification.propriedade_id || notification.dados?.propriedade_id;
    const plot = notification.plotName || notification.dados?.talhao_nome || notification.talhao_id || notification.dados?.talhao_id;
    const key = [
      normalizedNotificationText(notification.title || notification.titulo),
      normalizedNotificationText(notification.message || notification.mensagem),
      normalizeId(property),
      normalizeId(plot),
      timeKey,
    ].join('|');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function storageKey(userId) {
  return `${STORAGE_PREFIX}:${normalizeId(userId)}`;
}

function readStoredNotifications(userId) {
  if (typeof window === 'undefined' || !window.localStorage || !userId) return [];

  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey(userId)) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function notifyChange(userId) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { userId: normalizeId(userId) } }));
}

function writeStoredNotifications(userId, notifications) {
  if (typeof window === 'undefined' || !window.localStorage || !userId) return [];
  const scoped = (notifications || []).filter(item => normalizeId(item.recipientId) === normalizeId(userId));
  window.localStorage.setItem(storageKey(userId), JSON.stringify(scoped));
  notifyChange(userId);
  return scoped;
}

function createId(prefix = 'notification') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}:${crypto.randomUUID()}`;
  }
  return `${prefix}:${Date.now()}:${Math.random().toString(16).slice(2)}`;
}

export function listLocalOperationalNotifications(userId) {
  return readStoredNotifications(userId)
    .filter(item => normalizeId(item.recipientId) === normalizeId(userId) && !item.dismissedAt)
    .sort((first, second) => new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime());
}

export function getLocalOperationalUnreadCount(userId) {
  return listLocalOperationalNotifications(userId).filter(item => !item.readAt).length;
}

export function createLocalOperationalNotification(notification) {
  const recipientId = normalizeId(notification?.recipientId);
  if (!recipientId || !notification?.title || !notification?.message) return null;

  const current = readStoredNotifications(recipientId);
  const eventKey = notification.eventKey || createId('event');
  const duplicate = current.find(item => item.eventKey === eventKey);
  if (duplicate) return duplicate;

  const created = {
    id: notification.id || createId('notification'),
    eventKey,
    recipientId,
    type: notification.type || 'information',
    title: notification.title,
    message: notification.message,
    priority: notification.priority || 'normal',
    propertyId: normalizeId(notification.propertyId),
    propertyName: notification.propertyName || 'Propriedade não identificada',
    plotId: normalizeId(notification.plotId),
    plotName: notification.plotName || 'Talhão não identificado',
    orderId: notification.orderId || null,
    action: notification.action || 'caderno',
    source: 'local_demo',
    createdAt: notification.createdAt || new Date().toISOString(),
    readAt: notification.readAt || null,
  };

  writeStoredNotifications(recipientId, [created, ...current]);
  return created;
}

export function seedLocalOperationalNotifications({ userId, userName, properties = [] }) {
  const recipientId = normalizeId(userId);
  if (!recipientId || readStoredNotifications(recipientId).length > 0) {
    return listLocalOperationalNotifications(recipientId);
  }

  const eligibleProperties = properties.filter(property => (
    property.permission !== 'viewer'
    && property.plots?.length > 0
    && property.collaborators?.some(person => normalizeId(person.id) === recipientId)
  ));

  if (eligibleProperties.length === 0) return [];

  const now = Date.now();
  const first = eligibleProperties[0];
  const second = eligibleProperties[1] || first;
  const firstPlot = first.plots[0];
  const secondPlot = second.plots[Math.min(1, second.plots.length - 1)] || second.plots[0];
  const displayName = userName?.split(' ')[0] || 'Colaborador';

  const seeds = [
    {
      id: `seed:${recipientId}:assigned`,
      eventKey: `seed:${recipientId}:assigned`,
      recipientId,
      type: 'task_assigned',
      title: 'Nova atividade atribuída',
      message: `${displayName}, confirme o início da atividade e registre as evidências solicitadas.`,
      priority: 'urgent',
      propertyId: first.id,
      propertyName: first.name,
      plotId: firstPlot.id,
      plotName: firstPlot.name,
      orderId: 'OS-2026-0042',
      createdAt: new Date(now - 18 * 60 * 1000).toISOString(),
    },
    {
      id: `seed:${recipientId}:deadline`,
      eventKey: `seed:${recipientId}:deadline`,
      recipientId,
      type: 'deadline',
      title: 'Prazo operacional em atenção',
      message: 'A atividade prevista para hoje ainda precisa de atualização de andamento.',
      priority: 'high',
      propertyId: second.id,
      propertyName: second.name,
      plotId: secondPlot.id,
      plotName: secondPlot.name,
      orderId: 'OS-2026-0041',
      createdAt: new Date(now - 74 * 60 * 1000).toISOString(),
    },
    {
      id: `seed:${recipientId}:record`,
      eventKey: `seed:${recipientId}:record`,
      recipientId,
      type: 'information',
      title: 'Registro preservado no Caderno',
      message: 'A última execução foi vinculada corretamente à propriedade e ao talhão.',
      priority: 'normal',
      propertyId: first.id,
      propertyName: first.name,
      plotId: firstPlot.id,
      plotName: firstPlot.name,
      orderId: 'OS-2026-0038',
      createdAt: new Date(now - 25 * 60 * 60 * 1000).toISOString(),
      readAt: new Date(now - 24 * 60 * 60 * 1000).toISOString(),
    },
  ];

  seeds.forEach(seed => createLocalOperationalNotification(seed));
  return listLocalOperationalNotifications(recipientId);
}

export function markLocalOperationalNotificationRead(userId, notificationId, read = true) {
  const recipientId = normalizeId(userId);
  const current = readStoredNotifications(recipientId);
  const next = current.map(item => item.id === notificationId
    ? { ...item, readAt: read ? new Date().toISOString() : null }
    : item);
  writeStoredNotifications(recipientId, next);
  return next.find(item => item.id === notificationId) || null;
}

export function dismissLocalOperationalNotification(userId, notificationId) {
  const recipientId = normalizeId(userId);
  return writeStoredNotifications(recipientId, readStoredNotifications(recipientId).map(item => (
    item.id === notificationId
      ? { ...item, dismissedAt: new Date().toISOString() }
      : item
  )));
}

export function markAllLocalOperationalNotificationsRead(userId) {
  const recipientId = normalizeId(userId);
  const readAt = new Date().toISOString();
  return writeStoredNotifications(recipientId, readStoredNotifications(recipientId).map(item => ({
    ...item,
    readAt: item.readAt || readAt,
  })));
}

export function subscribeLocalOperationalNotifications(userId, listener) {
  if (typeof window === 'undefined') return () => {};
  const recipientId = normalizeId(userId);
  const handler = event => {
    if (normalizeId(event.detail?.userId) === recipientId) listener();
  };
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export const operationalNotificationStorage = Object.freeze({
  version: STORAGE_VERSION,
  prefix: STORAGE_PREFIX,
});
